import { SportRadarEvents, TrackingSessionKeys as Tracker } from './enums';
import facebookPixel from './facebookPixel';
import { GoogleTagManager } from './googleTagManager';
import { GOOGLE_TRACKING_ENABLED, prepareTrackingEventsResponse, eventAlreadyProcessedInSession } from './helpers';
import sportRadar from './sportRadar';
import {
    TrackingEventCallback,
    TrackingEventData,
    TrackingSessionKey,
    TrackingMetaData,
    TrackingEventCollection,
} from './types';

import SiteApi from 'Api/SiteApi/SiteApi';

document.addEventListener('DOMContentLoaded', async () => {
    const { metaData, trackingEvents } = prepareTrackingEventsResponse(await SiteApi.popTrackingEvents());
    // this query needs to be executed on every page load to clear session tracking events

    if (metaData.user_id && GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('set', { user_id: metaData.user_id });
    }

    if (metaData.affiliate_token && GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('set', { affiliate_token: metaData.affiliate_token });
        GoogleTagManager.gtag('event', 'affiliate_page_view_trigger');
    }

    function handleEvent(key: TrackingSessionKey, callback: TrackingEventCallback) {
        if (key in trackingEvents) {
            callback(metaData, trackingEvents[key], trackingEvents);
        }
    }

    handleEvent(Tracker.RegistrationSuccess, handleRegisterSuccess);
    handleEvent(Tracker.PurchaseSuccess, handlePurchaseSuccess);
    handleEvent(Tracker.LoggedIn, handleLoggedIn);
    handleEvent(Tracker.DepositFailure, handleDepositFailure);
    handleEvent(Tracker.DepositSuccess, handleDepositSuccess);
    handleEvent(Tracker.CompleteActivation, handleCompleteActivation);
    handleEvent(Tracker.ViewItem, handleViewItem);
    handleEvent(Tracker.BeginCheckout, handleBeginCheckout);
});

function handleRegisterSuccess(metaData: TrackingMetaData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'sign_up', { event_label: 'Register' });
    }

    if (facebookPixel.isActive) {
        facebookPixel.fbq('track', 'CompleteRegistration', { status: true });
    }

    if (sportRadar.isActive && metaData.user_id) {
        window.SportRadarEvents = { [SportRadarEvents.Registration]: metaData.user_id };
        sportRadar.triggerAll();
    }
}

function handlePurchaseSuccess(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'purchase', {
            event_label: 'Purchase',
            ...eventData,
        });
    }

    if (facebookPixel.isActive) {
        facebookPixel.fbq('track', 'Purchase', {
            currency: eventData.currency,
            value: eventData.value,
        });
    }
}

function handleLoggedIn(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'login', {
            event_label: 'Login',
            ...eventData,
        });
    }

    if (sportRadar.isActive && metaData.user_id) {
        window.SportRadarEvents = { [SportRadarEvents.Login]: metaData.user_id };
        sportRadar.triggerAll();
    }
}

function handleDepositFailure(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (eventAlreadyProcessedInSession(eventData)) {
        return;
    }

    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'deposit_failure', {
            event_label: 'DepositFailure',
            ...eventData,
        });
    }
}

function handleDepositSuccess(
    metaData: TrackingMetaData,
    eventData: TrackingEventData,
    trackingEvents: TrackingEventCollection
) {
    if (eventAlreadyProcessedInSession(eventData)) {
        return;
    }

    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'deposit', {
            event_label: 'Deposit',
            ...eventData,
        });
    }

    if (facebookPixel.isActive) {
        facebookPixel.fbq('trackCustom', 'Deposit', {
            currency: eventData.currency,
            value: eventData.value,
        });
    }

    if (sportRadar.isActive && metaData.user_id) {
        const depositData = {
            userId: metaData.user_id,
            transactionId: eventData.transaction_id,
            amount: eventData.value,
            currency: eventData.currency,
            label: Tracker.FTDSuccess in trackingEvents ? 'first_deposit' : undefined,
        };
        window.SportRadarEvents = { [SportRadarEvents.Deposit]: depositData };
        sportRadar.triggerAll();
    }
}

function handleCompleteActivation(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'complete_activation', {
            event_label: 'CompleteActivation',
            ...eventData,
        });
    }

    if (facebookPixel.isActive) {
        facebookPixel.fbq('track', 'CompleteActivation');
    }
}

function handleViewItem(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'view_item', {
            event_label: 'ViewItem',
            ...eventData,
        });
    }
}

function handleBeginCheckout(metaData: TrackingMetaData, eventData: TrackingEventData) {
    if (GOOGLE_TRACKING_ENABLED) {
        GoogleTagManager.gtag('event', 'begin_checkout', {
            event_label: 'BeginCheckout',
            ...eventData,
        });
    }
}
